import { Button, Grid } from '@mui/material';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import BlockTitle from 'components/layout/BlockTitle/BlockTitle';
import Loader from 'components/Loader/Loader';
import DiscountCodeModal from 'components/my/discountCodes/DiscountCodeModal';
import DiscountCodesTable from 'components/my/discountCodes/DiscountCodesTable/DiscountCodesTable';
import MyShopTabs, { MyShopTabName } from 'components/my/shop/MyShopTabs/MyShopTabs';
import NotFound from 'components/NotFound/NotFound';
import withAuth from 'components/with-auth/with-auth';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DiscountCodeService from 'services/discount-code.service';
import { DiscountCodeStatusType, DiscountCodeType } from 'types/discount-code.types';

const discountCodeService = new DiscountCodeService();

const DiscountCodes = () => {
  const { t } = useTranslation();

  const DISCOUNT_CODES_PER_ROW = 1;
  const DISCOUNT_CODES_PER_PAGE = DISCOUNT_CODES_PER_ROW * 10;

  const [discountCodes, setDiscountCodes] = useState([]);
  const [isDiscountCodeModalOpen, setDiscountCodeModalOpen] = useState(false);
  const [hasMoreDiscountCodes, setHasMoreDiscountCodes] = useState(true);
  const [isLoading, setLoading] = useState(true);

  const introSubtitle = t('pages.discount-codes.intro.sub-title');
  const introList: string[] = t('pages.discount-codes.intro.list', { returnObjects: true }) || [];
  const introNotSure = t('pages.discount-codes.intro.not-sure', { returnObjects: true }) || '';

  const loadMoreDiscountCodes = async (page: number) => {
    setLoading(true);

    // Request next set of discount codes
    try {
      const { list: nextDiscountCodes, total } = await discountCodeService.getList({
        offset: DISCOUNT_CODES_PER_PAGE * page,
        limit: DISCOUNT_CODES_PER_PAGE,
        sortDir: 'desc',
      });

      // Combine data for pagination
      let discountCodesToUpdate = [];

      if (page > 0) {
        discountCodesToUpdate = discountCodes.concat(nextDiscountCodes || []);
      } else {
        discountCodesToUpdate = nextDiscountCodes || [];
      }

      // Check for more
      setHasMoreDiscountCodes(!!nextDiscountCodes?.length && total > discountCodesToUpdate.length);

      // Save data
      setDiscountCodes(discountCodesToUpdate);
    } catch (e) {
      // Do nothing
    }

    setLoading(false);
  };

  const onAddDiscountCode = () => {
    // Reload list
    loadMoreDiscountCodes(0);
  };

  const toggleStatus = async (discountCode: DiscountCodeType) => {
    const status: DiscountCodeStatusType =
      discountCode.status === DiscountCodeStatusType.ACTIVE ? DiscountCodeStatusType.INACTIVE : DiscountCodeStatusType.ACTIVE;
    const discountCodeData = { status };

    try {
      await discountCodeService.update(discountCode.id, discountCodeData);

      // Update list manually
      const discountCodeToDeleteIndex = discountCodes.findIndex((v) => v.id === discountCode.id);

      if (discountCodeToDeleteIndex >= 0) {
        discountCode.status =
          discountCode.status === DiscountCodeStatusType.ACTIVE ? DiscountCodeStatusType.INACTIVE : DiscountCodeStatusType.ACTIVE;
        setDiscountCodes([...discountCodes]);
      }
    } catch (e) {
      // Do nothing
    }
  };

  const deleteDiscountCode = async (id: number) => {
    try {
      await discountCodeService.delete(id);

      // Update list manually
      const discountCodeToDeleteIndex = discountCodes.findIndex((discountCode) => discountCode.id === id);

      if (discountCodeToDeleteIndex >= 0) {
        discountCodes.splice(discountCodeToDeleteIndex, 1);
        setDiscountCodes([...discountCodes]);
      }
    } catch (e) {
      // Do nothing
    }
  };

  const onModalClose = () => {
    setDiscountCodeModalOpen(false);
  };

  return (
    <>
      <Header>
        <HeaderIconLogo icon="home" showLogo={false}>
          {t('pages.my-shop.discountCodes')}
        </HeaderIconLogo>
      </Header>

      <Grid className="vouchers-wrapper">
        {/* TABS */}
        <MyShopTabs value={MyShopTabName.DISCOUNT_CODES} />

        {/* INTRO */}
        <Grid className="intro-box">
          <BlockTitle className="intro-box-title">{t('pages.my-shop.discountCodes')}</BlockTitle>

          {introSubtitle && <div className="intro-box-sub-title">{introSubtitle}</div>}

          <ul className="intro-box-list">
            {introList.map((introListText, introListTextIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={'introlist-' + introListTextIndex}>
                <img src="/icons/icn-checkmark.png" alt="✓" />
                {introListText}
              </li>
            ))}
          </ul>

          <div className="intro-box-more-info" dangerouslySetInnerHTML={{ __html: introNotSure as string }} />
        </Grid>

        {/* CREATE PROMO CODE BUTTON */}
        <Grid container justifyContent="center" className="create-voucher-btn-container">
          <Button variant="contained" color="primary" className="btn-round" onClick={() => setDiscountCodeModalOpen(true)}>
            {t('pages.discount-codes.create-discount-code')}
          </Button>
        </Grid>

        <Grid container className="vouchers-list-wrapper" justifyContent="center">
          {/* NO DISCOUNT CODES MESSAGE */}
          {!discountCodes.length && !isLoading && (
            <NotFound
              target="vouchers"
              icon={<img src="/images/tag-gray.svg" alt="" />}
              title=""
              customMessage="pages.discount-codes.no-discount-codes"
            />
          )}

          {/* VOUCHERS LIST */}
          <InfiniteScroll
            dataLength={discountCodes.length}
            batchSize={DISCOUNT_CODES_PER_PAGE}
            loadMore={(page) => loadMoreDiscountCodes(page)}
            hasMore={hasMoreDiscountCodes}
            loader={<Loader />}
          >
            <DiscountCodesTable
              rows={discountCodes}
              onStatusChange={(discountCode) => toggleStatus(discountCode)}
              onDelete={(discountCodeId) => deleteDiscountCode(discountCodeId)}
            />
          </InfiniteScroll>
        </Grid>
      </Grid>

      <DiscountCodeModal isOpen={isDiscountCodeModalOpen} onAdd={() => onAddDiscountCode()} onClose={onModalClose} />
    </>
  );
};

export default withAuth(DiscountCodes);
